import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Section, LevelBadge, PageHeader, PageSkeleton, EmptyState } from "../../components/fields";
import { ReplyThread } from "../../components/ReplyThread";
import { MessageSquarePlus, CheckCheck, Inbox, ShieldCheck, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ReviewQueue() {
  const [data, setData] = useState(null);
  const [flags, setFlags] = useState([]);
  const [sent, setSent] = useState([]);
  const [coach, setCoach] = useState(null);
  const [comment, setComment] = useState("");
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const { user } = useAuth();
  const canSwitch = user?.role === "tag_admin";

  const load = () => {
    api.get("/supervisor/queue", { params: schoolId ? { school_id: schoolId } : {} }).then((r) => setData(r.data));
    api.get("/flags", { params: { status: "open" } }).then((r) => setFlags(schoolId ? r.data.filter((f) => f.school_id === schoolId) : r.data));
    api.get("/coaching-comments").then((r) => setSent(schoolId ? r.data.filter((c) => c.school_id === schoolId) : r.data));
  };
  useEffect(load, [schoolId]);
  useEffect(() => { if (canSwitch) api.get("/schools").then((r) => setSchools(r.data)); }, [canSwitch]);

  const sendCoaching = async () => {
    try {
      await api.post("/coaching-comments", { aide_id: coach.aide_id, student_id: coach.student_id, entry_type: coach.type, entry_id: coach.id, comment });
      toast.success("Coaching comment added");
      setCoach(null); setComment("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resolve = async (id) => {
    try {
      await api.put(`/flags/${id}/resolve`);
      toast.success("Flag resolved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <PageSkeleton />;

  return (
    <div className="space-y-6 stagger">
      <PageHeader title="Review Queue" subtitle="Recent documentation, open flags, and coaching — your supervision trail"
        actions={canSwitch && schools.length > 1 && (
          <select data-testid="queue-school-switcher" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}
            className="rounded-[10px] border border-[#E5E9EE] bg-white px-3 py-2 text-sm font-semibold text-[#132238] focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40">
            <option value="">All schools</option>
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )} />

      <Section title="Documentation completion by aide (last 5 days)">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.aide_stats.map((a) => (
            <div key={a.id} data-testid={`aide-stat-${a.id}`} className="border border-slate-100 rounded-lg p-4">
              <p className="text-sm font-semibold text-slate-800">{a.name}</p>
              <p className="text-xs text-slate-400 mt-0.5">{a.entries} entries · {a.active_days} active days</p>
              <p className="text-xs text-slate-400">Last entry: {a.last_entry ? a.last_entry.slice(0, 10) : "—"}</p>
            </div>
          ))}
        </div>
      </Section>

      {flags.length > 0 && (
        <Section title="Open flags">
          <div className="space-y-2.5">
            {flags.map((f) => (
              <div key={f.id} data-testid={`flag-${f.id}`} className="flex items-center gap-3 border border-slate-100 rounded-lg p-3.5 flex-wrap">
                <LevelBadge level={f.level} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">{f.category} · {f.student_name}</p>
                  <p className="text-xs text-slate-400">Opened {f.created_at.slice(0, 10)}</p>
                </div>
                <button data-testid={`resolve-flag-btn-${f.id}`} onClick={() => resolve(f.id)}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3.5 py-2 rounded-full transition-colors duration-150">
                  <CheckCheck size={14} /> Resolve
                </button>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Coaching sent — read receipts">
        <div className="space-y-2.5">
          {sent.length === 0 && <EmptyState icon={Inbox} title="No coaching comments sent yet" hint="Comments you add from the queue below appear here with a seen/not-seen receipt." />}
          {sent.map((c) => {
            const aideName = data.aide_stats.find((a) => a.id === c.aide_id)?.name || "Aide";
            return (
              <div key={c.id} data-testid={`sent-coaching-${c.id}`} className="border border-slate-100 rounded-lg p-3.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-slate-800">To {aideName}</p>
                  <span className="text-xs text-slate-400">{c.date} · by {c.supervisor_name}</span>
                  {c.acknowledged_at && (
                    <span data-testid={`sent-ack-${c.id}`} className="flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                      <CheckCircle2 size={12} /> Acknowledged
                    </span>
                  )}
                  {c.read_at ? (
                    <span data-testid={`receipt-seen-${c.id}`} className="ml-auto flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                      <Eye size={12} /> Seen {c.read_at.slice(0, 10)}
                    </span>
                  ) : (
                    <span data-testid={`receipt-unseen-${c.id}`} className="ml-auto flex items-center gap-1 text-[11px] font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                      <EyeOff size={12} /> Not seen yet
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600 mt-1.5">{c.comment}</p>
                <ReplyThread comment={c} onChanged={load} />
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Recent incidents">
        <div className="space-y-2.5">
          {data.incidents.length === 0 && <EmptyState icon={ShieldCheck} title="No recent incidents" hint="Behavior and concern entries from the last 5 days will appear here." />}
          {data.incidents.map((i) => (
            <div key={i.id} className="border border-slate-100 rounded-lg p-3.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-800">{i.behavior_type} · {i.student_name}</span>
                <LevelBadge level={i.alert_level} />
                <span className="text-xs text-slate-400">{i.date} · by {i.aide_name}</span>
              </div>
              <p className="text-sm text-slate-600 mt-1">{i.note}</p>
              <button data-testid={`coach-incident-btn-${i.id}`} onClick={() => setCoach({ aide_id: i.aide_id, student_id: i.student_id, type: "behavior_incident", id: i.id, label: `${i.behavior_type} · ${i.student_name}` })}
                className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900">
                <MessageSquarePlus size={14} /> Add coaching comment
              </button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Recent block entries">
        <div className="space-y-1.5">
          {data.entries.length === 0 && <EmptyState icon={Inbox} title="No entries yet" hint="Aide block documentation from the last 5 days will appear here for review." />}
          {data.entries.slice(0, 30).map((e) => (
            <div key={e.id} className="flex items-start justify-between gap-3 border-b border-slate-50 pb-2">
              <div className="min-w-0">
                <p className="text-sm text-slate-700"><span className="font-medium">{e.student_name}</span> · {e.block_name} · <span className="text-xs text-slate-400">{e.date} · {e.aide_name}</span></p>
                <p className="text-xs text-slate-500">{e.prompting_level} prompts · {e.engagement} · {e.work_completion}%{e.note ? ` — ${e.note}` : ""}</p>
              </div>
              <button data-testid={`coach-entry-btn-${e.id}`} onClick={() => setCoach({ aide_id: e.aide_id, student_id: e.student_id, type: "block_entry", id: e.id, label: `${e.block_name} · ${e.student_name}` })}
                className="text-sky-600 hover:text-sky-800 shrink-0 p-1.5"><MessageSquarePlus size={16} /></button>
            </div>
          ))}
        </div>
      </Section>

      {coach && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 sf-overlay flex items-end sm:items-center justify-center" onClick={() => setCoach(null)}>
          <div className="sf-sheet bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-slate-900">Coaching comment</h3>
            <p className="text-xs text-slate-500">{coach.label}</p>
            <textarea data-testid="coaching-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={4} autoFocus
              placeholder="Dated coaching feedback for this aide…"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            <button data-testid="submit-coaching-btn" onClick={sendCoaching} disabled={!comment}
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-full py-3 text-sm transition-colors duration-150 disabled:opacity-60">
              Add comment
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
